/**
 * Event accounts (docs/ops/event-role.md): freelance staff who run the rig
 * screens at one event and must read nothing else. tools/eventAccess.mjs
 * gives each one the allow-list role "event", a users/{uid} record with role
 * "event" and apps ["ingest"], and the claims {role: "rig_operator",
 * assignedJobs: [job document ids]}. The nyc-suite Firestore rules let such
 * an account read only its own users record, the rig view of each job in its
 * token's assignedJobs (rigJobs/{jobId}, one get each, never a list and
 * never jobs/{jobId} with its money and client), and those jobs' roster and
 * capture attempts. So the app, for an event account: finds the profile by
 * its own document (never by scanning users), loads no business data (no
 * equipment names, no feedback badge, no other app), opens Ingest's Rigs
 * only, and reads its jobs' rig views by id.
 */
import { auth, db, doc, getDoc } from "../firebase.js";

export const EVENT_ROLE = "event";

export const isEventUser = (user) => user?.role === EVENT_ROLE;

// Where the rig screens read a job: an event account, its rig view.
export const rigJobPath = (user, jobId) => (jobId ? `${isEventUser(user) ? "rigJobs" : "jobs"}/${jobId}` : null);

const isRefusal = (e) => e?.code === "permission-denied" || e?.code === "firestore/permission-denied";

// Job document ids as the rules and the functions accept them: plain ids.
const JOB_ID_RE = /^(?!__)[A-Za-z0-9_-]{1,128}$/;

/** The token's assignedJobs claim as a list of job ids (anything else: none). */
export function jobIdsFromClaims(claims) {
  const list = claims?.assignedJobs;
  if (!Array.isArray(list)) return [];
  return [...new Set(list.filter((j) => typeof j === "string" && JOB_ID_RE.test(j)))];
}

const sameEmail = (a, b) => typeof a === "string" && typeof b === "string" && a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * The Suite profile of the account signed in. Reads users/{uid} first: when
 * it is an event record for this email, that is the profile (an event
 * account may read nothing else in users). Otherwise the lookup by email,
 * exactly as before, for everyone else.
 *   loaders: { getOwn(uid), getByEmail(email) }, each resolving to a record
 *   ({ ...data, _docId }) or null. getOwn refused (permission-denied: not an
 *   event account under the rules) counts as null; any other failure (no
 *   connection) is thrown, so the app says "try again" instead of "Access
 *   denied".
 */
export async function loadProfile({ uid, email }, { getOwn, getByEmail }) {
  const own = await Promise.resolve().then(() => getOwn(uid)).catch((e) => {
    if (isRefusal(e)) return null;
    throw e;
  });
  if (isEventUser(own) && sameEmail(own.email, email)) return own;
  return getByEmail(email);
}

/**
 * The job ids this account's token lists. The token is refreshed first, so a
 * grant made after this sign-in shows without signing out; offline, the
 * token already held answers.
 */
export async function assignedJobIds(user = auth.currentUser) {
  if (!user) return [];
  let token;
  try {
    token = await user.getIdTokenResult(true);
  } catch {
    token = await user.getIdTokenResult();
  }
  return jobIdsFromClaims(token?.claims);
}

/**
 * The assigned jobs' rig views, read one by one: { jobs: [{ id, ...data }],
 * failed, refused }. failed counts reads that failed (no connection: the
 * picker offers Try again); refused counts reads the rules refused (the
 * grant or a deploy is not finished: the picker says to ask Ivin, never "no
 * job"). A view that is not there is left out. Never a query: the rules
 * refuse an event account any list.
 */
export async function loadAssignedJobs(ids, read = (id) => getDoc(doc(db, "rigJobs", id))) {
  let failed = 0;
  let refused = 0;
  const rows = await Promise.all(ids.map(async (id) => {
    try {
      const snap = await read(id);
      return snap.exists() ? { id: snap.id ?? id, ...snap.data() } : null;
    } catch (e) {
      if (isRefusal(e)) refused += 1;
      else failed += 1;
      return null;
    }
  }));
  return { jobs: rows.filter(Boolean), failed, refused };
}
